// ISDP CUSTOM: a tiny, framework-agnostic localStorage store powering the DELICATE
// Home personalization (Recently viewed + a "since your last visit" nudge).
//
// Identity-light by design — no teams, no server, no user identity. Everything
// here lives per-browser in localStorage, exactly like Backstage's own starred
// entities (see useStarredEntitiesList.ts). It is entirely additive: if nothing
// has been recorded (brand-new user, private mode, quota exceeded), every getter
// returns an empty/absent value so Home renders identically to today.
//
// Fail-safe: every read/write is wrapped so a throwing localStorage (Safari
// private mode, disabled storage, quota) degrades to a no-op rather than breaking
// the page.

const RECENT_KEY = 'devhub.home.recent';
const LAST_VISIT_KEY = 'devhub.home.lastVisit';

// Cap on how many recents we keep / surface. Small on purpose — this strip is
// visually subordinate to the hero.
const MAX_RECENTS = 6;

export type RecentType = 'doc' | 'catalog';

export interface RecentEntry {
  /** Which surface the entry came from — drives the icon on the Home strip. */
  type: RecentType;
  /** Human-readable title (document.title or a prettified path segment). */
  title: string;
  /** In-app path to navigate back to (used as the de-dupe key). */
  url: string;
  /** Epoch ms when it was last opened. Newest wins on de-dupe. */
  at: number;
}

// A cross-tab / same-tab change signal so the React hook can re-read without
// polling. localStorage's native 'storage' event only fires in *other* tabs, so
// we also dispatch this custom event after our own writes.
export const RECENT_EVENT = 'devhub:recent-activity';

function safeGetRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode / quota / disabled storage — no-op.
  }
}

function emitChange(): void {
  try {
    window.dispatchEvent(new Event(RECENT_EVENT));
  } catch {
    // Non-browser / SSR — no-op.
  }
}

/**
 * Reads the recents list: capped at {@link MAX_RECENTS}, de-duped by url, newest
 * first. Returns an empty array on any parse/storage failure (fail-safe).
 */
export function getRecents(): RecentEntry[] {
  const raw = safeGetRaw(RECENT_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Validate shape defensively — anything malformed is dropped.
    return parsed
      .filter(
        (e): e is RecentEntry =>
          e &&
          typeof e.url === 'string' &&
          typeof e.title === 'string' &&
          typeof e.at === 'number' &&
          (e.type === 'doc' || e.type === 'catalog'),
      )
      .slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

/**
 * Records (or refreshes) a recently-viewed entry. De-dupes by url — an existing
 * entry for the same url is moved to the front with the new timestamp. Silently
 * ignores empty/invalid input and any storage failure.
 */
export function pushRecent(entry: {
  type: RecentType;
  title: string;
  url: string;
  at?: number;
}): void {
  if (!entry || !entry.url || !entry.title) return;
  const next: RecentEntry = {
    type: entry.type,
    title: entry.title,
    url: entry.url,
    at: entry.at ?? Date.now(),
  };

  const existing = getRecents().filter(e => e.url !== next.url);
  const list = [next, ...existing].slice(0, MAX_RECENTS);

  safeSetRaw(RECENT_KEY, JSON.stringify(list));
  emitChange();
}

/**
 * Epoch ms of the previous visit, or null if this is the first ever visit (or
 * storage is unavailable). Reads live storage — see {@link getLastVisitSnapshot}
 * for the value the "since your last visit" nudge should use.
 */
export function getLastVisit(): number | null {
  const raw = safeGetRaw(LAST_VISIT_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// The last-visit timestamp as it stood the *first* time anything read it this
// page-load, captured before markVisited() overwrites it. This makes the
// "N updates since your last visit" count stable regardless of whether Root's
// markVisited() effect or Home's read runs first.
let visitSnapshot: number | null | undefined;

/**
 * The last-visit timestamp captured on first read this page-load. Stable across
 * the session even after {@link markVisited} advances the stored value. Returns
 * null on the very first visit (or when storage is unavailable).
 */
export function getLastVisitSnapshot(): number | null {
  if (visitSnapshot === undefined) {
    visitSnapshot = getLastVisit();
  }
  return visitSnapshot;
}

/**
 * Stamps "now" as the last-visit marker. Called once per session (Root mount).
 * Captures the pre-existing value into the session snapshot first so the
 * "since your last visit" nudge keeps showing the previous visit's delta.
 * No-op on storage failure.
 */
export function markVisited(at: number = Date.now()): void {
  // Ensure the snapshot reflects the value from *before* this stamp.
  getLastVisitSnapshot();
  safeSetRaw(LAST_VISIT_KEY, String(at));
}
